/**
 * Cadastro de ativos: números que filtram, busca, lista e detalhe com o ativo em 3D — Nexora Grid (frontend, US-406, US-4603).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * v2.0 (Sprint 46, protótipo aprovado em 02/11/2026): topo com KPIs que filtram (total, a validar, em alarme, sem
 * comunicação, sem telemetria), busca com `/`, filtros no endereço com pílulas, lista densa (cartões no celular) e o
 * painel de detalhe ao lado com a prévia 3D do equipamento, a última leitura e as ações. ↑ ↓ andam na lista; Enter, ou
 * clicar de novo na linha selecionada, abre a ficha. Mesmas permissões e chamadas de antes (`GET /v1/ativos`), mais a
 * telemetria e os alarmes só para mostrar o estado (somente leitura).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { fmtValor, nomeMedida, unidadeMedida } from "../cco/medidas";
import { BarraFiltros, CabecalhoTela, EstadoAlarme, EstadoVazio, ErroCarregar, Esqueleto, FaixaKpis, ListaDetalhe, Segmentado, Selo, TabelaDensa,
  useLembrarEndereco, useParametro, type Coluna, type Estado } from "../componentes/Tela";
import { IconeTipo } from "../componentes/IconeTipo";
import { PreviaAtivo3D } from "../componentes/PreviaAtivo3D";
import { casaBusca, idadeRelativa, ordenar, type Ordem } from "../componentes/regrasTela";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { contarKpis, estadoDoAtivo, leituraPrincipal, ORDEM_ESTADO, passaKpi, type FiltroKpi, type LeituraAtivo } from "./regrasCadastro";
import { SITUACOES, TIPOS, type Ativo } from "./tipos";
import { useTelemetriaAtivos } from "./useTelemetriaAtivos";

type Linha = { ativo: Ativo; estado: Estado; leitura: LeituraAtivo | null; alarme?: string };
const TOM_SITUACAO: Record<string, "ok" | "atencao" | "ruim"> = { validado: "ok", pendente: "atencao", rejeitado: "ruim" };
const ROTULO_KPI: Record<string, string> = { a_validar: "A validar", alarme: "Em alarme", semcom: "Sem comunicação", semtel: "Sem telemetria" };

export function LeituraCurta({ leitura, estado }: { leitura: LeituraAtivo | null; estado: Estado }) {
  if (!leitura) return <span className="nx-suave">—</span>;
  return (
    <span className="tl-mono">{fmtValor(leitura.medida, leitura.valor)} {unidadeMedida(leitura.medida)}
      <small className="nx-suave"> {estado === "semcom" ? "último · " : ""}{idadeRelativa(leitura.timestamp)}</small></span>
  );
}

function Detalhe({ linha, aoFechar }: { linha: Linha; aoFechar: () => void }) {
  const { ativo, estado, leitura } = linha;
  const d = ativo.dados;
  return (
    <>
      <PreviaAtivo3D tipo={d.tipo} identificacao={d.identificacao} estado={estado} rotulo={TIPOS[d.tipo] ?? d.tipo} />
      <div className="tl-det-corpo">
        <div className="tl-det-titulo"><div style={{ flex: 1, minWidth: 0 }}><h2>{d.identificacao}</h2><p>{d.nome || TIPOS[d.tipo]}</p></div>
          <button type="button" className="tl-fantasma tl-fechar" onClick={aoFechar} aria-label="Fechar o detalhe">×</button></div>
        <div className="tl-linha-acoes"><Selo tom={TOM_SITUACAO[ativo.situacao]}>{SITUACOES[ativo.situacao]}</Selo><EstadoAlarme estado={estado} /></div>
        {leitura ? (
          <div className="medida-ativo">
            <small>{nomeMedida(leitura.medida)}{linha.alarme ? ` · ${linha.alarme}` : ""}</small>
            <span className="nx-suave">{idadeRelativa(leitura.timestamp)}</span>
            <strong>{fmtValor(leitura.medida, leitura.valor)}<em>{unidadeMedida(leitura.medida)}</em></strong>
          </div>
        ) : (
          <div className="medida-ativo"><small>{d.device_id ? "Sem leitura recente" : "Sem telemetria"}</small>
            <span className="nx-suave" style={{ gridColumn: "1 / -1" }}>{d.device_id
              ? `O dispositivo ${d.device_id} não mandou leitura nas últimas horas.`
              : "Nenhum dispositivo ligado a este ativo. Ligue um na ficha para ver as leituras aqui."}</span></div>
        )}
        <dl className="tl-kv">
          <dt>Tipo</dt><dd>{TIPOS[d.tipo] ?? d.tipo}</dd><dt>Setor</dt><dd>{d.setor || "—"}</dd>
          <dt>Dispositivo</dt><dd>{d.device_id || "—"}</dd>
          <dt>Coordenadas</dt><dd>{d.latitude.toFixed(5).replace(".", ",")} · {d.longitude.toFixed(5).replace(".", ",")}</dd>
          <dt>Ficha técnica</dt><dd>{ativo.pendencias_ficha.length ? `faltam ${ativo.pendencias_ficha.length} campos` : "completa"}</dd>
          <dt>Atualizado</dt><dd>{idadeRelativa(ativo.atualizado_em)} · versão {ativo.versao}</dd>
        </dl>
        <div className="tl-linha-acoes">
          <Link className="nx-botao nx-botao-principal" to={`/cadastro/${ativo.id}`}>Abrir ficha</Link>
          {d.device_id && <Link className="nx-botao" to={`/cco/equipamento/${encodeURIComponent(d.device_id)}`}>Ver no CCO</Link>}
          <Link className="nx-botao" to={`/rede?foco=${encodeURIComponent(d.identificacao)}`}>Ver na Rede</Link>
        </div>
      </div>
    </>
  );
}

export function Lista() {
  const { usuario, empresa } = useSessao();
  const navegar = useNavigate();
  const [ativos, setAtivos] = useState<Ativo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useParametro("busca");
  const [tipo, setTipo] = useParametro("tipo");
  const [setor, setSetor] = useParametro("setor");
  const [situacao, setSituacao] = useParametro("situacao");
  const [kpi, setKpi] = useParametro("kpi");
  const [sel, setSel] = useParametro("sel");
  const [vista, setVista] = useParametro("vista", "lista");
  const [ordem, setOrdem] = useState<Ordem>({ coluna: "estado", crescente: true });
  const [painelAberto, setPainelAberto] = useState(false);
  const telemetria = useTelemetriaAtivos(empresa);
  useLembrarEndereco();

  const carregar = () => {
    if (!empresa) return;
    setAtivos(null);
    setErro(null);
    api<Ativo[]>(`/v1/ativos?empresa=${empresa}`).then(setAtivos).catch((e) => setErro(String(e.message ?? e)));
  };
  useEffect(carregar, [empresa]);  // eslint-disable-line react-hooks/exhaustive-deps

  const linhas = useMemo<Linha[]>(() => (ativos ?? []).map((a) => {
    const tel = a.dados.device_id ? telemetria.get(a.dados.device_id) : undefined;
    return { ativo: a, estado: estadoDoAtivo(a, tel), leitura: leituraPrincipal(a.dados.tipo, tel), alarme: tel?.alarme };
  }), [ativos, telemetria]);
  const kpis = useMemo(() => contarKpis(linhas.map((l) => ({ situacao: l.ativo.situacao, estado: l.estado, setor: l.ativo.dados.setor }))), [linhas]);
  const setores = useMemo(() => [...new Set(linhas.map((l) => l.ativo.dados.setor).filter(Boolean))].sort(), [linhas]);
  const visiveis = useMemo(() => {
    const filtradas = linhas.filter(({ ativo: a, estado }) => (!situacao || a.situacao === situacao) && (!tipo || a.dados.tipo === tipo)
      && (!setor || a.dados.setor === setor) && passaKpi(kpi as FiltroKpi, a.situacao, estado)
      && casaBusca(busca, a.dados.identificacao, a.dados.nome, TIPOS[a.dados.tipo], a.dados.setor, a.dados.device_id));
    const valor = (l: Linha) => ordem.coluna === "estado" ? ORDEM_ESTADO[l.estado] : ordem.coluna === "setor" ? `${l.ativo.dados.setor} ${l.ativo.dados.identificacao}`
      : ordem.coluna === "tipo" ? TIPOS[l.ativo.dados.tipo] ?? l.ativo.dados.tipo : ordem.coluna === "leitura" ? l.leitura?.timestamp ?? null : l.ativo.dados.identificacao;
    return ordenar(ordenar(filtradas, (l) => l.ativo.dados.identificacao), valor, ordem.crescente);
  }, [linhas, situacao, tipo, setor, kpi, busca, ordem]);
  const selecionada = visiveis.find((l) => l.ativo.id === sel) ?? visiveis[0] ?? null;

  const pilulas = [
    ...(kpi ? [{ chave: "kpi", texto: ROTULO_KPI[kpi] ?? kpi }] : []), ...(tipo ? [{ chave: "tipo", texto: TIPOS[tipo] ?? tipo }] : []),
    ...(setor ? [{ chave: "setor", texto: `Setor ${setor}` }] : []), ...(situacao ? [{ chave: "situacao", texto: SITUACOES[situacao] ?? situacao }] : []),
    ...(busca ? [{ chave: "busca", texto: `“${busca}”` }] : []),
  ];
  const tirar = (chave: string) => ({ kpi: setKpi, tipo: setTipo, setor: setSetor, situacao: setSituacao, busca: setBusca } as Record<string, (v: string) => void>)[chave]?.("");
  const limpar = () => { setKpi(""); setTipo(""); setSetor(""); setSituacao(""); setBusca(""); };
  const colunas: Coluna<Linha>[] = [
    { chave: "id", titulo: "Identificação", ordenavel: true, celula: (l) => (
      <div className="tl-ident"><IconeTipo tipo={l.ativo.dados.tipo} /><div><b>{l.ativo.dados.identificacao}</b><span>{l.ativo.dados.nome || "—"}</span></div></div>) },
    { chave: "tipo", titulo: "Tipo", ordenavel: true, celula: (l) => TIPOS[l.ativo.dados.tipo] ?? l.ativo.dados.tipo },
    { chave: "setor", titulo: "Setor", ordenavel: true, celula: (l) => <span className="tl-mono">{l.ativo.dados.setor || "—"}</span> },
    { chave: "situacao", titulo: "Cadastro", celula: (l) => <Selo tom={TOM_SITUACAO[l.ativo.situacao]}>{SITUACOES[l.ativo.situacao]}</Selo> },
    { chave: "estado", titulo: "Estado", ordenavel: true, celula: (l) => <EstadoAlarme estado={l.estado} /> },
    { chave: "leitura", titulo: "Última leitura", ordenavel: true, celula: (l) => <LeituraCurta leitura={l.leitura} estado={l.estado} /> },
    { chave: "ficha", titulo: "Ficha", celula: (l) => (l.ativo.pendencias_ficha.length ? <span className="nx-suave">faltam {l.ativo.pendencias_ficha.length}</span> : "completa") },
  ];
  const podeCriar = !!usuario && (pode.editarCadastro(usuario) || pode.cadastrarEmCampo(usuario));

  return (
    <section className={`tl-pagina cadastro-novo${vista === "cartoes" ? " vista-cartoes" : ""}`}>
      <CabecalhoTela migalhas={[{ texto: "Cadastro", para: "/cadastro" }, { texto: "Ativos" }]} titulo="Cadastro de ativos"
        subtitulo={usuario && empresa ? usuario.nomes_empresas?.[empresa] ?? empresa : undefined}
        acoes={<>
          {usuario && pode.editarCadastro(usuario) && <Link className="nx-botao" to="/cadastro/importar">Importar DWG/DXF/EPANET</Link>}
          {podeCriar && <Link className="nx-botao nx-botao-principal" to="/cadastro/novo">+ Novo ativo</Link>}
        </>} />
      {erro ? <ErroCarregar mensagem={`Não foi possível carregar os ativos: ${erro}`} aoTentar={carregar} /> : !ativos ? <Esqueleto linhas={8} /> : (
        <>
          <FaixaKpis ativo={kpi} aoEscolher={setKpi} itens={[
            { chave: "", rotulo: "Ativos cadastrados", valor: kpis.total, detalhe: `${kpis.setores} setores` },
            { chave: "a_validar", rotulo: "A validar", valor: kpis.a_validar, detalhe: "pendentes da engenharia" },
            { chave: "alarme", rotulo: "Em alarme", valor: kpis.alarme, detalhe: `◆ ${kpis.criticos} crítico · ▲ ${kpis.altos} alto`, tom: kpis.criticos ? "critico" : "alarme" },
            { chave: "semcom", rotulo: "Sem comunicação", valor: kpis.semcom, detalhe: "sem leitura recente", tom: "semcom" },
            { chave: "semtel", rotulo: "Sem telemetria", valor: kpis.semtel, detalhe: "sem dispositivo ligado" },
          ]} />
          <BarraFiltros busca={busca} aoBuscar={setBusca} placeholder="Buscar por identificação, nome, tipo, setor ou dispositivo"
            pilulas={pilulas} aoTirar={tirar} aoLimpar={limpar}>
            <label htmlFor="f-tipo">Tipo</label>
            <select id="f-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Todos</option>{Object.entries(TIPOS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>
            <label htmlFor="f-setor">Setor</label>
            <select id="f-setor" value={setor} onChange={(e) => setSetor(e.target.value)}>
              <option value="">Todos</option>{setores.map((s) => <option key={s}>{s}</option>)}</select>
            <Segmentado rotulo="Situação do cadastro" valor={situacao} aoMudar={setSituacao}
              opcoes={[{ valor: "", texto: "Todas" }, { valor: "validado", texto: "Validados" }, { valor: "pendente", texto: "Pendentes" },
                { valor: "rejeitado", texto: "Rejeitados" }]} />
          </BarraFiltros>
          <ListaDetalhe aberto={painelAberto}
            lista={(
              <TabelaDensa rotulo="Ativos" colunas={colunas} linhas={visiveis.slice(0, 1000)} chave={(l) => l.ativo.id} selecionada={selecionada?.ativo.id ?? null}
                aoSelecionar={(l) => { setSel(l.ativo.id); setPainelAberto(true); }} aoAbrir={(l) => navegar(`/cadastro/${l.ativo.id}`)}
                ordem={ordem} aoOrdenar={setOrdem}
                cabecalho={<><span><b>{visiveis.length}</b> de {linhas.length} ativos{visiveis.length > 1000 ? " (mostrando 1.000: use os filtros)" : ""}</span>
                  <span className="espaco" style={{ flex: 1 }} />
                  <Segmentado rotulo="Forma da lista" valor={vista} aoMudar={setVista} opcoes={[{ valor: "lista", texto: "Lista" }, { valor: "cartoes", texto: "Cartões" }]} /></>}
                vazio={<EstadoVazio titulo="Nenhum ativo com esses filtros" texto="Tire um filtro ou cadastre o ativo que falta."
                  acao={pilulas.length ? <button type="button" className="nx-botao" onClick={limpar}>Limpar filtros</button>
                    : podeCriar ? <Link className="nx-botao" to="/cadastro/novo">Cadastrar ativo</Link> : undefined} />}
                cartao={(l) => (
                  <>
                    <div className="tl-linha-acoes" style={{ justifyContent: "space-between" }}>
                      <div className="tl-ident"><IconeTipo tipo={l.ativo.dados.tipo} /><div><b>{l.ativo.dados.identificacao}</b>
                        <span>{TIPOS[l.ativo.dados.tipo]} · {l.ativo.dados.setor || "sem setor"}</span></div></div>
                      <Selo tom={TOM_SITUACAO[l.ativo.situacao]}>{SITUACOES[l.ativo.situacao]}</Selo></div>
                    <span style={{ fontSize: 13 }}>{l.ativo.dados.nome || "—"}</span>
                    <div className="tl-linha-acoes" style={{ justifyContent: "space-between" }}><EstadoAlarme estado={l.estado} /><LeituraCurta leitura={l.leitura} estado={l.estado} /></div>
                  </>
                )} />
            )}
            detalhe={selecionada ? <Detalhe linha={selecionada} aoFechar={() => setPainelAberto(false)} /> : undefined} />
        </>
      )}
    </section>
  );
}
